import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import colors from '@packages/core/styles/colors';
import { createUseStyles } from 'react-jss';
import { Icon, Button, Typography, AlertBar } from '@packages/ui/shared';
import VinSessionDropdown from './vin-session-dropdown';
import { useVinSessionState } from '@web/vin-sessions/context/vin-sessions.hooks';
import { VIN } from '../types';
import NoImageSubaruCar from '../assets/2CFlat_Hor_White.png';
import { httpClient } from '@web/services/singletons';
import { openDocumentTab, withCdnEntryFile } from '@web/vin-sessions/utils/open-document-tab';
import { useAuthState } from '@packages/contexts/auth';
import { SkeletonLoader } from '@web/components/skeleton-loader';

type VinSessionBannerProps = {};

const useStyles = createUseStyles({
    '@global': {
        '@keyframes moveCar': {
            '0%': {
                transform: 'translateX(-100vw)',
            },
            '100%': {
                transform: 'translateX(0)',
            },
        },
        '@keyframes moveRectangle': {
            '0%': {
                transform: 'translateX(-100vw)',
            },
            '100%': {
                transform: 'translateX(0)',
            },
        },
    },
    bannerContainer: {
        backgroundColor: colors.blueOneHover,
        minHeight: 88,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 140,
    },
    rectangle: {
        width: 82,
        height: 38,
        backgroundColor: '#E6E6E6',
        border: '1px solid #ccc',
        zIndex: 100,
        animation: 'moveRectangle 0.5s ease-out forwards',
    },
    image: {
        width: '100%',
        height: '100%',
        position: 'absolute',
        animation: 'moveCar 0.5s ease-out forwards',
    },
    detailsContainer: {
        height: 40,
        display: 'flex',
        alignItems: 'center',
        gap: 20,
    },
    details: {
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'wrap',
    },
    statusContainer: {
        height: 18,
        width: 131,
        borderRadius: 4,
        padding: '1px 8px 1px 8px',
        display: 'flex',
        alignContent: 'center',
        gap: 4,
        color: colors.black,
    },
    button: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginLeft: 5,
        gap: 4,
    },
    imageContainer: {
        width: 160,
        height: 100,
        position: 'absolute',
        top: -11,
        left: 32,
        overflow: 'hidden',
        zIndex: 101,
    },
    imageContainerError: {
        width: 150,
        height: 50,
        position: 'absolute',
        top: 19,
        left: 55,
        overflow: 'hidden',
        zIndex: 101,
    },
    imageHidden: {
        opacity: 0,
    },
    imageVisible: {
        opacity: 1,
        transition: 'opacity 0.3s ease-in',
    },
    skeletonBanner: {
        backgroundColor: colors.blueOneHover,
        minHeight: 88,
        display: 'flex',
        alignItems: 'center',
        padding: '0 24px 0 240px',
        gap: 20,
    },
    skeletonBlock: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
    },
});

const VinSessionBanner: FC<VinSessionBannerProps> = (props) => {
    const classes = useStyles();
    const { token } = useAuthState();
    const { selectedVin } = useVinSessionState();
    const dropDownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);

    const [vinData, setVinData] = useState<VIN>();
    const [vinDetailsDropDown, setVinDetailsDropDown] = useState<boolean>(false);
    const [isImageError, setIsImageError] = useState<boolean>(false);
    const [isImageLoaded, setIsImageLoaded] = useState<boolean>(false);
    const [showingBatteryUpdateForm, setShowingBatteryUpdateForm] = useState(false);
    const prevImageUrlRef = useRef<string>();

    useEffect(() => {
        if (selectedVin != undefined) {
            const newImageUrl = selectedVin.vehicleImageUrl;
            if (newImageUrl !== prevImageUrlRef.current) {
                setIsImageError(false);
                setIsImageLoaded(false);
                prevImageUrlRef.current = newImageUrl;
            }
            if (selectedVin.evBattery?.bin) {
                setVinData({
                    ...selectedVin,
                    vinRecord: { ...selectedVin.vinRecord, bin: selectedVin.evBattery.bin },
                });
            } else {
                setVinData(selectedVin);
            }
        }
    }, [selectedVin]);

    const handleClickOutside = useCallback(
        (event: MouseEvent) => {
            if (showingBatteryUpdateForm) {
                return;
            }
            if (dropDownRef.current && !dropDownRef.current.contains(event.target as Node)) {
                if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
                    setVinDetailsDropDown(false);
                }
            }
        },
        [showingBatteryUpdateForm]
    );

    const handleImageError = () => {
        setIsImageError(true);
        setIsImageLoaded(true);
    };

    const handleImageLoad = () => {
        setIsImageLoaded(true);
    };

    const formatEngine = (record: VIN['vinRecord'] | undefined): string => {
        if (!record) return '—';
        const { engineSize, engine, fuel, turboIndicator } = record;
        if (!engineSize && !engine && !fuel) return '—';
        const size = engineSize != null ? `${engineSize} L` : '';
        const turbo = turboIndicator === 'true' ? ' Turbo' : '';
        const cylinders = engine != null ? ` ${engine} Cylinders` : '';
        const fuelType = fuel != null ? ` ${fuel}` : '';
        const result = `${size}${turbo}${cylinders}${fuelType}`.trim();
        return result || '—';
    };

    const showFallback = isImageError || !vinData?.vehicleImageUrl;
    const imageSrc = showFallback ? NoImageSubaruCar : vinData.vehicleImageUrl;

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showingBatteryUpdateForm]);

    if (!vinData) {
        return (
            <div className={classes.skeletonBanner}>
                {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className={classes.skeletonBlock}>
                        <SkeletonLoader type="text" width={80} height={12} />
                        <SkeletonLoader type="text" width={100} height={14} />
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div>
            <div className={classes.bannerContainer}>
                <div>
                    <div className={classes.rectangle} />
                    <div className={showFallback ? classes.imageContainerError : classes.imageContainer}>
                        <img
                            src={imageSrc}
                            className={`${classes.image} ${isImageLoaded ? classes.imageVisible : classes.imageHidden}`}
                            onError={handleImageError}
                            onLoad={handleImageLoad}
                            alt="Vehicle"
                        />
                    </div>
                </div>
                <div className={classes.detailsContainer}>
                    <div className={classes.details}>
                        <Typography variant="caption" style={{ color: '#FFFFFF' }}>
                            Car Line
                        </Typography>
                        <Typography variant="caption4" style={{ color: '#FFFFFF' }}>
                            {vinData.vinRecord.model || '—'}
                        </Typography>
                    </div>
                    <div className={classes.details}>
                        <Typography variant="caption" style={{ color: '#FFFFFF' }}>
                            Model Year
                        </Typography>
                        <Typography variant="caption4" style={{ color: '#FFFFFF' }}>
                            {vinData.vinRecord.modelYear || '—'}
                        </Typography>
                    </div>
                    <div className={classes.details}>
                        <Typography variant="caption" style={{ color: '#FFFFFF' }}>
                            Engine
                        </Typography>
                        <Typography variant="caption4" style={{ color: '#FFFFFF' }}>
                            {formatEngine(vinData.vinRecord)}
                        </Typography>
                    </div>
                    <div className={classes.details} style={{ gap: 4 }}>
                        <Typography variant="caption" style={{ color: '#FFFFFF' }}>
                            Trim Level
                        </Typography>
                        <Typography variant="caption4" style={{ color: '#FFFFFF' }}>
                            {vinData.vinRecord.trimDescription || '—'}
                        </Typography>
                    </div>
                    <div className={classes.details}>
                        <Typography variant="caption" style={{ color: '#FFFFFF' }}>
                            Transmission Type
                        </Typography>
                        <Typography variant="caption4" style={{ color: '#FFFFFF' }}>
                            {vinData.vinRecord.driveTrain || vinData.vinRecord.transmissionType
                                ? `${vinData.vinRecord.driveTrain || ''} ${vinData.vinRecord.transmissionType || ''}`.trim()
                                : '—'}
                        </Typography>
                    </div>
                    <div className={classes.details} ref={buttonRef}>
                        <Button
                            style={{
                                backgroundColor: !vinDetailsDropDown ? '#436CA1' : '#1F518F',
                                maxWidth: 136,
                                padding: 8,
                            }}
                            onPress={() => {
                                setVinDetailsDropDown(!vinDetailsDropDown);
                            }}
                        >
                            <div className={classes.button}>
                                <Typography
                                    id="test1"
                                    variant="caption3"
                                    style={{ textAlign: 'center', color: '#FFFFFF' }}
                                >
                                    {vinDetailsDropDown ? 'Hide details' : 'All details'}
                                </Typography>
                                <Icon
                                    name={!vinDetailsDropDown ? 'arrow-small-down' : 'arrow-small-up'}
                                    style={{ color: '#FFFFFF', marginTop: !vinDetailsDropDown ? 1 : 0 }}
                                />
                            </div>
                        </Button>
                    </div>

                    <div style={{ height: 40, width: 1, backgroundColor: '#FFFFFF' }} />

                    <div
                        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                        onClick={async () => {
                            await openDocumentTab(async () => {
                                const res = await fetch(
                                    `${httpClient.vinUrl}/docs/cdn-url-by-params?modelYear=${selectedVin?.vinRecord.modelYear}&carlineId=${selectedVin?.carlineId}`,
                                    {
                                        headers: {
                                            'X-Subcomm-Access-Token': token,
                                        },
                                        credentials: 'include',
                                    }
                                );
                                const data = await res.json();
                                // The backend returns the CDN bundle folder; append the index.html
                                // entry (same as the outside Service Diagnostics click) so CloudFront
                                // can serve it instead of bouncing into a redirect loop.
                                return withCdnEntryFile(data?.documentUrl);
                            });
                        }}
                    >
                        <Typography
                            id="test1"
                            variant="caption3"
                            style={{ fontSize: 15, fontWeight: 600, textAlign: 'center', color: '#FFFFFF' }}
                        >
                            {'Service Diagnostics'}
                        </Typography>
                        <Icon name="open-in-new" color="white" />
                    </div>
                </div>
            </div>
            {vinDetailsDropDown && (
                <VinSessionDropdown
                    vinData={vinData}
                    dropDownRef={dropDownRef}
                    showHideBinUpdateCallback={(showing) => {
                        if (!showing) {
                            setVinDetailsDropDown(false);
                        }
                        setShowingBatteryUpdateForm(showing);
                    }}
                />
            )}
        </div>
    );
};

export default VinSessionBanner;
